// Pure helpers for deriving Insights filter/scope state.
// The single source for the default date window and the boot-time scope resolution, so the
// boot path (entrypoints/insights.js) and the store (loadData) can never drift.

import moment from 'moment-timezone';
import { omit } from 'lodash-es';
import { INSIGHTS } from '~/app_constants';
import { formatInternalDate } from '~/utils/time_utils';

// Resolves a raw URL `tab` to an allowed tab: `highlights` is manager-only (gated by canSeeTeam),
// and an unknown/missing tab falls back to the default. Single source of tab gating so the boot
// path can normalize the URL once and the store resolver reads a value that cannot diverge.
export function resolveAvailableTab(urlTab, { canSeeTeam = false } = {}) {
  const availableTabs = INSIGHTS.tabs.filter((tab) => tab !== 'highlights' || canSeeTeam);

  return availableTabs.includes(urlTab) ? urlTab : INSIGHTS.defaultTab;
}

export function resolveBootTimeZone({ urlTimeZone = null, sessionTimeZone = null, tzOptions = [] } = {}) {
  return [urlTimeZone, sessionTimeZone, tzOptions[0]?.for, moment.tz.guess()]
    .find((timeZone) => timeZone && moment.tz.zone(timeZone));
}

// Builds the stable :key for the Unusual-activity widget container (used by both the highlights
// preview and the UA tab). Drops the nested `date.time_zone` - a redundant duplicate of the
// authoritative top-level `filters.time_zone` that the date picker stamps into `filters.date` on
// mount - plus any caller-listed UI-only keys, then sorts the remaining keys so logically-equal
// scopes always serialize to the same string (a bare property reorder must not re-key and refetch).
// A real time-zone change still rewrites the top-level `filters.time_zone` and rehashes.
export function unusualActivityWidgetKey(filters, excludedKeys = []) {
  const rest = omit(filters || {}, [...excludedKeys, 'date.time_zone']);
  const sorted = Object.fromEntries(Object.entries(rest).sort(([a], [b]) => a.localeCompare(b)));

  return JSON.stringify(sorted);
}

// Parses a saved member_filter preference (e.g. "42_user_id") into { id, type }, or null when it
// is absent or the all-members sentinel.
function parseMemberFilter(savedMemberFilter) {
  if (!savedMemberFilter || savedMemberFilter === 'all_user_id') {
    return null;
  }

  const match = savedMemberFilter.match(/^(.+)_(team_id|user_id)$/);

  return match ? { id: match[1], type: match[2] } : null;
}

/**
 * Computes the default date window for an Insights tab as raw moment objects.
 * Callers own timezone construction, formatting, and clamping - this function only
 * decides which window a given (tab, view) should start with.
 *
 * @param {Object} params
 * @param {string} params.tab - Active tab ('performance', 'unusual-activity', 'timeline', ...).
 * @param {moment} params.now - "Now" already expressed in the target timezone.
 * @param {number} [params.startWeekOn] - Organization start-of-week setting (server-side weekday).
 * @param {boolean} [params.isUserView] - True for an individual member view (vs team/all-members).
 * @returns {{ from: moment, to: moment }} The default window as moment clones of `now`.
 */
export function defaultDateWindow({ tab, now, startWeekOn = 0, isUserView = false }) {
  if (tab === 'unusual-activity') {
    return { from: now.clone().startOf('month'), to: now.clone().endOf('day') };
  }

  if (tab === 'timeline') {
    // The multi-user timeline renders a single day; only an individual view opens on the week.
    if (!isUserView) {
      return { from: now.clone(), to: now.clone() };
    }

    const isoWeekday = toMomentIsoWeekday(startWeekOn);

    return {
      from: now.clone().startOfIsoWeek(isoWeekday),
      to: now.clone().endOfIsoWeek(isoWeekday),
    };
  }

  return { from: now.clone().startOfIsoWeek(toMomentIsoWeekday(startWeekOn)), to: now.clone().endOf('day') };
}

// The gated no-insights UA preview always renders a fixed trailing 60-day window (it hides the
// date picker). Kept here so the boot entrypoint and TheInsights derive the exact same window and
// cannot drift. Returns raw moments; callers own formatting (mirrors defaultDateWindow).
export function noInsightsUaPreviewWindow(now) {
  return { from: now.clone().subtract(60, 'days').startOf('day'), to: now.clone().endOf('day') };
}

// Converts a { from, to } moment window (as returned by defaultDateWindow / noInsightsUaPreviewWindow)
// into internal date strings. The single "window -> internal strings" step shared by the boot path
// and TheInsights so the two cannot format the same window differently.
export function windowToInternal({ from, to }) {
  return { from: formatInternalDate(from), to: formatInternalDate(to) };
}

/**
 * Resolves the initial filter scope from the URL on boot, returning the filters to apply and the
 * member-dropdown selection to set. Pure: all environment lookups (preferences, session, getters)
 * are passed in by the caller. This is the single source for the boot-time scope derivation.
 *
 * @param {URL} location - The current location (its searchParams drive the derivation).
 * @param {Object} context
 * @param {boolean} context.canSeeTeam - Whether the viewer can choose team/all-members scope.
 * @param {boolean} context.isTeamLead - Team leads pre-select their first team.
 * @param {number}  context.teamsTotal - Count of managed teams (gates the team-lead pre-select).
 * @param {number|string|null} context.firstTeamId - Id of the first managed team.
 * @param {number|string|null} context.baseUserId - The viewer's own user id (non-team fallback).
 * @param {Object} context.currentFilters - Already-seeded filters (e.g. the boot date window) to keep.
 * @param {boolean} context.remoteVsOfficeAvailable - Whether the RvO toggle is available.
 * @param {boolean} context.remoteVsOfficePreference - Saved RvO preference (already org-gated).
 * @param {number}  context.startWeekOn - Organization start-of-week (for the timeline default).
 * @param {boolean} context.sessionHasFrom - Whether session filters already carry a `from`.
 * @param {string|null} context.savedMemberFilter - Saved member_filter preference (e.g.
 *   `123_user_id`); restored as the scope only when the URL pins no member/team.
 * @returns {{ filters: Object, selectedFilter: string|null }}
 */
export function resolveInitialScope(location, {
  canSeeTeam = false,
  isTeamLead = false,
  teamsTotal = 0,
  firstTeamId = null,
  baseUserId = null,
  currentFilters = {},
  remoteVsOfficeAvailable = false,
  remoteVsOfficePreference = false,
  startWeekOn = 0,
  sessionHasFrom = false,
  savedMemberFilter = null,
} = {}) {
  const params = location.searchParams;
  const tzParam = params.get('time_zone');
  let teamId;
  let userId = baseUserId;
  let restoredFromSaved = false;

  if (canSeeTeam) {
    // Managers begin on All members, or on the user/team named in the URL. With no explicit URL
    // scope, fall back to the saved member_filter preference, then a team lead's first team.
    let urlTeamId = params.has('team_id') ? params.get('team_id') : null;
    let urlUserId = params.has('user_id') ? params.get('user_id') : null;
    const savedScope = urlTeamId === null && urlUserId === null ? parseMemberFilter(savedMemberFilter) : null;

    if (savedScope) {
      restoredFromSaved = true;
      urlTeamId = savedScope.type === 'team_id' ? savedScope.id : null;
      urlUserId = savedScope.type === 'user_id' ? savedScope.id : null;
    }

    teamId = urlTeamId;

    if (teamId === null && urlUserId === null && isTeamLead && teamsTotal) {
      teamId = firstTeamId;
    }
    userId = urlUserId && !teamId ? urlUserId : teamId ? null : 'all';
  }

  // Gate the tab here too (not just at boot) so this resolver is self-defending: any caller
  // reaching it with a raw/disallowed URL tab still gets an allowed value, never a divergent one.
  const tab = resolveAvailableTab(params.get('tab'), { canSeeTeam });
  const confidenceLevels = params.has('confidence_levels') ? params.get('confidence_levels').split(',') : null;
  let remoteVsOffice = null;

  if (remoteVsOfficeAvailable) {
    if (params.has('remote_vs_office')) {
      remoteVsOffice = params.get('remote_vs_office') === 'true' ? true : null;
    } else if (remoteVsOfficePreference) {
      remoteVsOffice = true;
    }
  }

  let date;

  // The individual timeline opens on the current week when neither the URL nor session pins a range.
  if (tab === 'timeline' && !params.has('from') && !params.has('to') && !sessionHasFrom) {
    const isUserView = !canSeeTeam || (userId && userId !== 'all');

    if (isUserView) {
      date = windowToInternal(defaultDateWindow({ tab: 'timeline', now: moment.tz(tzParam), startWeekOn, isUserView: true }));
    }
  }

  const filters = {
    ...currentFilters,
    user_id: userId,
    time_zone: tzParam,
    team_id: teamId,
    tab,
    starts_at: params.has('starts_at') ? params.get('starts_at') : null,
    activity_type: params.has('activity_type') ? params.get('activity_type') : null,
    index: params.has('index') ? params.get('index') : null,
    ...(confidenceLevels && { confidence_levels: confidenceLevels }),
    remote_vs_office: remoteVsOffice,
    ...(date && { date }),
  };

  let selectedFilter = null;

  if (params.has('team_id') || params.has('user_id') || restoredFromSaved) {
    if (filters.team_id) {
      selectedFilter = `${filters.team_id}_team_id`;
    } else if (filters.user_id && filters.user_id !== 'all') {
      selectedFilter = `${filters.user_id}_user_id`;
    }
  }

  return { filters, selectedFilter };
}
